import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { AuthStoreService } from '@motivforce/mx-library-angular';
import { of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { User } from '../model/user';
import { UserStoreService } from '../store/user-store.service';

@Injectable({
  providedIn: 'root',
})
export class UserGuard implements CanActivate {
  constructor(private authStore: AuthStoreService, private userStore: UserStoreService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.authStore.isRegularUser$.pipe(
      filter((isRegularUser) => isRegularUser != null),
      switchMap((isRegularUser) => {
        if (isRegularUser || this.authStore.getImpersonatedUserId() != null) {
          return this.userStore.accessStatus$.pipe(
            filter(Boolean),
            map(() => {
              if (this.isBlockedUser()) {
                return this.router.parseUrl('/not-allowed');
              }

              if (this.userIsNotEnrolled() && state.url !== '/enrolment') {
                return this.router.parseUrl('/enrolment');
              }

              return true;
            })
          );
        }

        return of(true);
      })
    );
  }

  private isBlockedUser(): boolean {
    const user = this.authStore.userSettings?.user as User;
    return (
      !!this.userStore.accessStatus &&
      (this.userStore.accessStatus.userEnrollingInFinalMonth ||
        this.userStore.accessStatus.userIsFromBlockedCountry ||
        (this.userStore.accessStatus.companyInExcludedList &&
          user?.acceptedTermsConditions === false &&
          this.userStore.accessStatus.termsAndConditionsAccepted === false) ||
        user.statusId === 3)
    );
  }

  private userIsNotEnrolled(): boolean {
    const user = this.authStore.userSettings?.user as User;
    if (user?.acceptedTermsConditions === false) {
      // Make sure we update the user settings if tnc are accepted
      if (this.userStore.accessStatus!.termsAndConditionsAccepted === true) {
        this.authStore.getUserSettings();
      }

      return this.userStore.accessStatus!.termsAndConditionsAccepted === false;
    }

    return false;
  }
}
